import React, { Fragment, useEffect } from 'react';
import { DashboardSkeleton } from 'pages/ReportsPage/components/AnalyticsDashboards/Skeleton';
import { useAnalyticsFilterStore } from 'pages/ReportsPage/store/analyticsFilterView.context';
import { useFulfillmentDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/store/fulfillmentDashboard.store';
import { useFulfillmentKpiCards } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/useFulfillmentKpiCards';
import { KpiStatCardRow } from 'pages/ReportsPage/components/AnalyticsDashboards/KpiStatCardRow';
import { WidgetCardWrapper } from 'pages/ReportsPage/components/AnalyticsDashboards/WidgetCardWrapper';
import { UnfulfilledByPositionChart } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/UnfulfilledByPositionChart';
import { CalloutRateChart } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/CalloutRateChart';
import { CalloutFulfillmentChart } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/CalloutFulfillmentChart';
import { FulfillmentByTimeframeChart } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/FulfillmentByTimeframeChart';
import { FulfillmentByPositionChart } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/FulfillmentByPositionChart';
import { PeakCalloutHoursChart } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/PeakCalloutHoursChart';
import { ResolutionSpeedChart } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/ResolutionSpeedChart';
import { UnfulfilledShiftsTable } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/UnfulfilledShiftsTable';
import { LastMinuteCancellationsTable } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/LastMinuteCancellationsTable';
import { ExportCsvButton } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/ExportCsvButton';
import {
  DashboardContainer,
  DashboardHeader,
  DashboardContent,
  DashboardTitle,
  WidgetGrid,
  FullWidthSection,
  DashboardErrorContainer,
} from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.styled';
import { FilterBar } from 'pages/ReportsPage/components/AnalyticsDashboards/FilterBar';
import { Flex } from '@bookjane2/bookjane-design-library';

export const FulfillmentDashboard: React.FC = () => {
  const fetchData = useFulfillmentDashboardStore((s) => s.fetchData);
  const reset = useFulfillmentDashboardStore((s) => s.reset);
  const isLoading = useFulfillmentDashboardStore((s) => s.isLoading);
  const error = useFulfillmentDashboardStore((s) => s.error);
  const dateRange = useAnalyticsFilterStore((s) => s.dateRange);
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const employmentTypes = useAnalyticsFilterStore((s) => s.employmentTypes);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const lastMinuteCancellationTotalCount = useFulfillmentDashboardStore(
    (s) => s.lastMinuteCancellationTotalCount,
  );
  const kpiCards = useFulfillmentKpiCards();
  const showSkeleton = isLoading;
  const lastMinuteCancellationsSubtitle = `Shifts cancelled ≤4 hours before scheduled start time · ${lastMinuteCancellationTotalCount.toLocaleString()} total`;

  useEffect(() => {
    return fetchData(dateRange, locationIds, positions, employmentTypes);
  }, [fetchData, dateRange, locationIds, employmentTypes, positions]);

  useEffect(() => {
    return () => reset();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DashboardContainer aria-label="Workforce Fulfillment & Operations Dashboard">
      <DashboardHeader>
        <Flex justifyContent="space-between" alignItems="center" gap="16px">
          <DashboardTitle>Workforce Fulfillment & Operations</DashboardTitle>
          <ExportCsvButton />
        </Flex>
      </DashboardHeader>
      <FilterBar />
      <DashboardContent>
        {error ? (
          <div aria-live="assertive">
            <DashboardErrorContainer>{error}</DashboardErrorContainer>
          </div>
        ) : showSkeleton ? (
          <DashboardSkeleton kpiCount={4} widgetCount={6} />
        ) : (
          <Fragment>
            <KpiStatCardRow cards={kpiCards} />
            <WidgetGrid>
              <WidgetCardWrapper
                title="Number of shifts fulfilled per positions"
                subtitle="Fulfillment rate by position"
              >
                <UnfulfilledByPositionChart />
              </WidgetCardWrapper>
              <WidgetCardWrapper
                title="Call Out Rate"
                subtitle="Assigned Shifts vs Call Out Shifts by position"
              >
                <CalloutRateChart />
              </WidgetCardWrapper>
            </WidgetGrid>
            <FullWidthSection>
              <WidgetCardWrapper
                title="Fulfillment rate for call outs"
                subtitle="Fulfillment rate and Avg Fulfillment Time (Hrs) by position"
              >
                <CalloutFulfillmentChart />
              </WidgetCardWrapper>
            </FullWidthSection>
            <WidgetGrid>
              <WidgetCardWrapper
                title="Fulfillment percentage by timeframe"
                subtitle="Percentage of shifts fulfilled within each timeframe"
              >
                <FulfillmentByTimeframeChart />
              </WidgetCardWrapper>
              <WidgetCardWrapper
                title="Fulfillment by position"
                subtitle="Fulfillment percentage per position"
              >
                <FulfillmentByPositionChart />
              </WidgetCardWrapper>
            </WidgetGrid>
            <WidgetGrid>
              <WidgetCardWrapper
                title="Peak Call Out Hours"
                subtitle="Number of call out shifts starting in each window"
              >
                <PeakCalloutHoursChart />
              </WidgetCardWrapper>
              <WidgetCardWrapper
                title="Average speed to resolve call outs"
                subtitle="Hours from call out creation to acceptance"
              >
                <ResolutionSpeedChart />
              </WidgetCardWrapper>
            </WidgetGrid>
            <FullWidthSection>
              <WidgetCardWrapper
                title="Unfulfilled shifts detail"
                subtitle="Shifts that remained unfulfilled after call out completion"
              >
                <UnfulfilledShiftsTable />
              </WidgetCardWrapper>
            </FullWidthSection>
            <FullWidthSection>
              <WidgetCardWrapper
                title="Last-Minute Cancellations"
                subtitle={lastMinuteCancellationsSubtitle}
              >
                <LastMinuteCancellationsTable />
              </WidgetCardWrapper>
            </FullWidthSection>
          </Fragment>
        )}
      </DashboardContent>
    </DashboardContainer>
  );
};
